import { POSTHOG_UI_HOST, type AnalyticsApp, type OnboardingStep } from '@remote-medium/shared';
import posthog, { type PostHogConfig, type PostHogInterface } from 'posthog-js';

// Paths whose second segment is a credential (consent / unsubscribe links).
const TOKEN_PATH = /^(\/(?:consent|unsubscribe)\/)[^/]+/;

/**
 * Strips what must never reach PostHog from a URL or path: the query string and
 * hash (reset-password and magic-link tokens) and the credential segment of
 * `/consent/:token` and `/unsubscribe/:id`. Handles both absolute URLs and
 * relative paths (`$pathname`); anything else (e.g. `$direct`) is returned as is.
 */
export function scrubUrl(raw: string): string {
  const relative = raw.startsWith('/') && !raw.startsWith('//');
  try {
    const url = new URL(raw, relative ? 'https://placeholder.invalid' : undefined);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname.replace(TOKEN_PATH, '$1:token');
    return relative ? url.pathname : url.toString();
  } catch {
    return raw;
  }
}

// Every SDK property that can hold a URL or path: $current_url, $pathname,
// $referrer, $session_entry_url, $initial_current_url, $prev_pageview_pathname...
const URL_KEY = /url|pathname|referrer|href/i;

/** Scrubs URL-like string properties, including inside `$set` / `$set_once`. */
export function scrubProperties(props: Record<string, unknown>, depth = 0): void {
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'string') {
      if (URL_KEY.test(key) && value) props[key] = scrubUrl(value);
    } else if (depth < 2 && value && typeof value === 'object' && !Array.isArray(value)) {
      scrubProperties(value as Record<string, unknown>, depth + 1);
    }
  }
}

/** Strips credentials from every URL-ish property before an event leaves the browser. */
function beforeSend<T extends { properties?: Record<string, unknown> } | null>(event: T): T {
  if (event?.properties) scrubProperties(event.properties);
  return event;
}

type App = Exclude<AnalyticsApp, 'api'>;

// --- Events -----------------------------------------------------------------
// The only events the browser apps may send. Properties are fixed values
// (enums, field names): never free text, emails, names, CV contents or ids.
// `undefined` = no properties. Anonymous and storage-free, so they are sent
// for every visitor (docs/OPEN_QUESTIONS.md Q33).

export const CTA_LOCATIONS = ['header', 'hero', 'cta_band', 'footer', 'role_page'] as const;
export const CTA_TARGETS = ['join', 'signin', 'company_signup', 'company_login'] as const;

export interface AnalyticsEvents {
  cookie_choice: { choice: 'accept' | 'decline' };
  cta_clicked: {
    location: (typeof CTA_LOCATIONS)[number];
    target: (typeof CTA_TARGETS)[number];
  };
  magic_link_requested: { mode: 'signup' | 'signin' };
  onboarding_step_completed: { step: OnboardingStep };
  /** `fields` are form field names (e.g. `fullName`), never their values. */
  onboarding_validation_failed: { step: OnboardingStep; fields: string[] };
  profile_submitted: undefined;
  cv_upload_result: { result: 'ok' | 'too_large' | 'wrong_type' | 'failed' };
  consent_answered: { answer: 'yes' | 'no' };
  takedown_submitted: undefined;
  company_signup_submitted: undefined;
  role_posted: undefined;
  checkout_started: { product: 'account_fee' | 'credits' };
}

let active = false;

/** Sends one anonymous event. No-op when analytics is off (no key, dev). */
export function track<E extends keyof AnalyticsEvents>(
  event: E,
  ...[props]: AnalyticsEvents[E] extends undefined ? [] : [AnalyticsEvents[E]]
): void {
  if (active) posthog.capture(event, props);
}

const isMember = <T extends readonly string[]>(list: T, v: string | undefined): v is T[number] =>
  v !== undefined && list.includes(v);

/** `<a data-cta="hero:join">` → cta_clicked. Anything not on the allowlists is ignored. */
function trackCtaClicks(): void {
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const [location, target] = (
      e.target.closest('[data-cta]')?.getAttribute('data-cta') ?? ''
    ).split(':');
    if (isMember(CTA_LOCATIONS, location) && isMember(CTA_TARGETS, target)) {
      track('cta_clicked', { location, target });
    }
  });
}

const BASE_CONFIG = {
  ui_host: POSTHOG_UI_HOST,
  person_profiles: 'identified_only',
  capture_pageview: 'history_change',
  capture_exceptions: true,
  autocapture: false,
  disable_session_recording: true,
  disable_surveys: true,
  before_send: beforeSend,
} satisfies Partial<PostHogConfig>;

interface InitOptions {
  /** Public project key. Absent → analytics stays off (dev, previews). */
  key: string | undefined;
  app: App;
  /**
   * Where events are sent: the first-party relay on the web Worker, e.g.
   * `/ingest` (same origin) or `https://remotemedium.com/ingest` (dashboards).
   */
  apiHost: string;
}

/**
 * Dashboards (admin, company): cookieless, no storage, no consent needed.
 * Pageviews and exceptions only: no replay, no autocapture, no surveys.
 */
export function initAnalytics({ key, app, apiHost }: InitOptions): void {
  if (!key) return;
  active = true;
  posthog.init(key, {
    ...BASE_CONFIG,
    api_host: apiHost,
    persistence: 'memory',
    loaded: (ph) => ph.register({ app }),
  });
}

// --- Public site: consent-based ---------------------------------------------

export type ConsentStatus = 'granted' | 'denied' | 'pending';

/** Fired on `document` once PostHog is initialised, so the banner can read the status. */
export const ANALYTICS_READY_EVENT = 'rm:analytics-ready';

// Allowlist, not blocklist: replay and autocapture only run where the page
// shows public content. Never /profile, /consent/*, /unsubscribe/*, /signin
// (candidate data or credentials on screen). /join is recorded with every
// input masked.
const RECORDABLE_PATHS = [/^\/$/, /^\/(companies|join)\/?$/, /^\/roles(\/[^/]+)?\/?$/];

export function isRecordablePath(pathname: string): boolean {
  return RECORDABLE_PATHS.some((re) => re.test(pathname));
}

let ready = false;

/** Turns on the consent-gated features (replay, autocapture, surveys) or shuts them off. */
function applyConsent(ph: PostHogInterface): void {
  const granted = ph.get_explicit_consent_status() === 'granted';
  const record = granted && isRecordablePath(window.location.pathname);
  ph.set_config({ autocapture: record, disable_surveys: !granted });
  if (record) ph.startSessionRecording();
  else ph.stopSessionRecording();
}

/**
 * Public site. Until the visitor accepts: cookieless pageview counts and error
 * reports only (`cookieless_mode: 'on_reject'`), no storage, no recording.
 * After "Accept": normal PostHog persistence, plus session replay (inputs
 * masked) and autocapture on the public pages, and surveys. The choice is kept
 * in localStorage (strictly necessary to remember it).
 */
export function initConsentAnalytics({ key, app, apiHost }: InitOptions): void {
  if (!key) return;
  active = true;
  trackCtaClicks();
  posthog.init(key, {
    ...BASE_CONFIG,
    api_host: apiHost,
    persistence: 'localStorage+cookie',
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    opt_out_capturing_persistence_type: 'localStorage',
    session_recording: { maskAllInputs: true },
    loaded: (ph) => {
      ph.register({ app });
      applyConsent(ph);
      ready = true;
      document.dispatchEvent(new Event(ANALYTICS_READY_EVENT));
    },
  });
}

/** `null` when analytics is off (no key) or not yet initialised. */
export function getConsentStatus(): ConsentStatus | null {
  return ready ? posthog.get_explicit_consent_status() : null;
}

export function setConsent(granted: boolean): void {
  if (!ready) return;
  if (granted) posthog.opt_in_capturing();
  else posthog.opt_out_capturing();
  applyConsent(posthog);
}
